import { OpeningDetailResponse } from 'api-clients/monolith/models/OpeningDetailResponse';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ExternalLinkIcon } from '@/components/icons/external-link';
import { Button } from '@/components/ui/button';
import { FormSelect } from '@/components/ui/select';
import { TruncatedText } from '@/components/ui/truncated-text';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useAccountSlug } from '@/hooks/use-account-slug';
import { domainMeta } from '@/utils/domain-meta';

import { STATE_OPTIONS } from '../utils/constants';
import { DescriptionSection } from './description-section';
import { useJobConstants } from './hooks/use-job-constants';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';

interface BaseFieldsProps {
  opening: OpeningDetailResponse;
  payDisplay: string | null;
  isInVonqChannel: boolean;
  isZipRecruiterSelected: boolean;
  onRequestRefreshBanner?: () => void;
}

export function BaseFields({
  opening,
  payDisplay,
  isInVonqChannel,
  isZipRecruiterSelected,
  onRequestRefreshBanner,
}: BaseFieldsProps) {
  const intl = useIntl();
  const accountSlug = useAccountSlug();
  const { getJobType, getJobHours } = useJobConstants();
  const { hookForm } = useJobPostingContext();
  const {
    control,
    formState: { errors },
    register,
  } = hookForm;

  const jobDetailErrors = errors?.jobDetails?.[opening.external_id];
  const stateCodeErrorMessage = jobDetailErrors?.stateCode?.message;

  const locationFieldLabels = {
    locationAddress: intl.formatMessage({
      defaultMessage: 'Location',
      description: 'Location field label used in validation error message',
    }),
    postalCode: intl.formatMessage({
      defaultMessage: 'Postal code',
      description: 'Postal code field label used in validation error message',
    }),
    city: intl.formatMessage({
      defaultMessage: 'City',
      description: 'City field label used in validation error message',
    }),
  } as const;

  const missingLocationFieldLabels = (
    Object.keys(locationFieldLabels) as Array<keyof typeof locationFieldLabels>
  )
    .filter(key => jobDetailErrors?.[key]?.message)
    .map(key => locationFieldLabels[key]);

  const requiredFieldLabels = {
    industry: intl.formatMessage({
      defaultMessage: 'Industry',
      description: 'Industry of the job',
    }),
    jobHours: intl.formatMessage({
      defaultMessage: 'Job hours',
      description: 'Job hours of the job',
    }),
    jobType: intl.formatMessage({
      defaultMessage: 'Job type',
      description: 'Job type of the job',
    }),
    experience: intl.formatMessage({
      defaultMessage: 'Years of experience',
      description: 'Years of experience of the job',
    }),
  } as const;

  const missingRequiredFieldLabels = (
    Object.keys(requiredFieldLabels) as Array<keyof typeof requiredFieldLabels>
  )
    .filter(key => jobDetailErrors?.[key]?.message)
    .map(key => requiredFieldLabels[key]);

  const jobDetailsErrorMessage =
    missingRequiredFieldLabels.length > 0
      ? intl.formatMessage(
          {
            defaultMessage:
              '{count, plural, one {{fields} is required} other {{fields} are required}}',
            description:
              'Combined error shown beneath the job details summary when one or more required job fields are missing.',
          },
          {
            count: missingRequiredFieldLabels.length,
            fields: intl.formatList(missingRequiredFieldLabels, {
              type: 'conjunction',
            }),
          },
        )
      : undefined;

  const jobSettingsUrl = `${domainMeta.monolithOrigin}/${accountSlug}/openings/${opening.slug}`;
  const locationSettingsUrl = `${domainMeta.monolithOrigin}/${accountSlug}/locations#/${opening.location_external_id}/edit`;

  return (
    <>
      {/* Posting title */}
      <Input
        {...register(`jobDetails.${opening.external_id}.title`)}
        label={intl.formatMessage({
          defaultMessage: 'Title',
          description: 'Label for the title input for the job',
        })}
        placeholder={intl.formatMessage({
          defaultMessage: 'Enter title for the job',
          description: 'Placeholder for the title input for the job',
        })}
        required
        error={errors?.jobDetails?.[opening.external_id]?.title?.message}
      />

      {/* Job details */}
      <div className="flex flex-col gap-4">
        <div className="flex justify-between gap-4">
          <Typography variant="subtitle2">
            <FormattedMessage
              defaultMessage="Job details"
              description="Title for the job details section"
            />
          </Typography>
          <EditButton
            toUrl={jobSettingsUrl}
            onClick={() => onRequestRefreshBanner?.()}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-4">
            <PropertyItem
              title={requiredFieldLabels.industry}
              value={opening.job_industry}
              isRequired={isInVonqChannel}
            />

            <PropertyItem
              title={requiredFieldLabels.jobHours}
              value={getJobHours(opening.job_hours)}
              isRequired={isInVonqChannel}
            />

            <PropertyItem
              title={requiredFieldLabels.jobType}
              value={getJobType(opening.job_type)}
              isRequired={isInVonqChannel}
            />

            <PropertyItem
              title={requiredFieldLabels.experience}
              value={opening.years_of_experience}
              isRequired={isInVonqChannel}
            />

            <PropertyItem
              title={intl.formatMessage({
                defaultMessage: 'Pay',
                description: 'Pay of the job',
              })}
              value={payDisplay}
            />
          </div>
          {jobDetailsErrorMessage && (
            <Typography variant="small1" className="!text-fx-rose">
              {jobDetailsErrorMessage}
            </Typography>
          )}
        </div>
      </div>

      <Separator />

      {/* Location */}
      <div className="flex flex-col gap-4">
        <div className="flex justify-between gap-4">
          <Typography variant="subtitle2">
            <FormattedMessage
              defaultMessage="Location details"
              description="Title for the location details section"
            />
          </Typography>
          <EditButton
            toUrl={locationSettingsUrl}
            onClick={() => onRequestRefreshBanner?.()}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-4">
            <PropertyItem
              title={intl.formatMessage({
                defaultMessage: 'Location',
                description: 'Location of the job',
              })}
              value={
                opening.remote
                  ? intl.formatMessage({
                      defaultMessage: 'Remote',
                      description: 'Remote location of the job',
                    })
                  : opening.location_address
              }
              isRequired={isInVonqChannel}
            />

            <PropertyItem
              title={intl.formatMessage({
                defaultMessage: 'Brand',
                description: 'Brand name of the job',
              })}
              value={opening.brand_name}
            />
          </div>
          {missingLocationFieldLabels.length > 0 && (
            <Typography variant="small1" className="!text-fx-rose">
              {intl.formatMessage(
                {
                  defaultMessage:
                    '{count, plural, one {{fields} is required} other {{fields} are required}}',
                  description:
                    'Combined error shown beneath the location summary when one or more required location fields are missing.',
                },
                {
                  count: missingLocationFieldLabels.length,
                  fields: intl.formatList(missingLocationFieldLabels, {
                    type: 'conjunction',
                  }),
                },
              )}
            </Typography>
          )}
        </div>

        {opening.remote && isZipRecruiterSelected && (
          <FormSelect
            name={`jobDetails.${opening.external_id}.stateCode`}
            control={control}
            options={[...STATE_OPTIONS]}
            isMultiSelect={false}
            placeholder={intl.formatMessage({
              defaultMessage: 'Select state',
              description: 'Placeholder for state select',
            })}
            label={intl.formatMessage({
              defaultMessage: 'Hiring State',
              description: 'Label for state select',
            })}
            description={intl.formatMessage({
              defaultMessage:
                'Note: Used for job board distribution and targeting. This does not restrict applicant eligibility.',
              description: 'Description for state select',
            })}
            required
            error={stateCodeErrorMessage}
          />
        )}
      </div>

      <Separator />

      <DescriptionSection openingId={opening.external_id} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function PropertyItem({
  title,
  value,
  isRequired = false,
}: {
  title: string;
  value: string | number | null;
  isRequired?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 overflow-hidden">
      <Typography variant="caption" className="!text-fx-neutral-500">
        {title} {isRequired && <span className="text-fx-rose">*</span>}
      </Typography>
      <TruncatedText variant="body1">
        {value ? value.toString() : '-'}
      </TruncatedText>
    </div>
  );
}

function EditButton({
  toUrl,
  onClick,
}: {
  toUrl: string;
  onClick?: () => void;
}) {
  return (
    <Button variant="ghost" className="h-6 rounded-md" asChild>
      <Link
        to={toUrl}
        target="_blank"
        onClick={() => {
          onClick?.();
        }}
      >
        <ExternalLinkIcon className="text-fx-primary" />
        <FormattedMessage
          defaultMessage="Edit in Hire"
          description="Button to open the opening settings in Hire for editing"
        />
      </Link>
    </Button>
  );
}
